<script setup lang="ts">
/**
 * Pläne als EIN ruhiges Panel: Free und Premium, per Hairline getrennt statt
 * schwebender Karten — Premium als getönte Spalte. Keine Preise auf der Seite:
 * Premium ist ein Server-Abo im Discord-Store, dort stehen Preis und Steuern.
 * Bewusst ohne UPricingPlans-Default-Optik; Buttons folgen dem
 * Pill-Shape-Lock der Seite.
 *
 * Entrance: das Panel steigt, die Pläne folgen gestaffelt, die Feature-Zeilen
 * laufen von links ein und ihre Häkchen ploppen nacheinander — zuletzt die
 * CTAs. Premium bekommt einen kurzen Licht-Schub aus seiner Tönung.
 */
const sectionRef = ref<HTMLElement | null>(null)
useReveal(sectionRef)
useSpotlight(sectionRef)

useEntrance(sectionRef, ({ q, tl }) => {
  tl.from(q('.pricing-panel'), { opacity: 0, y: 28, duration: 0.8, clearProps: 'transform' }, 0)
  tl.from(q('.pricing-glow'), { opacity: 0, scale: 0.85, duration: 1.4, ease: 'power2.out', clearProps: 'transform' }, 0)
  tl.from(q('.pricing-plan'), { opacity: 0, y: 16, duration: 0.7, stagger: 0.16, clearProps: 'transform' }, 0.15)
  tl.from(q('.plan-billing'), { opacity: 0, x: -8, duration: 0.5, stagger: 0.16, clearProps: 'transform' }, 0.35)
  tl.from(q('.plan-feature'), { opacity: 0, x: -10, duration: 0.45, stagger: 0.05, clearProps: 'transform' }, 0.5)
  tl.from(q('.plan-check'), { scale: 0, duration: 0.4, ease: 'back.out(3)', stagger: 0.05, clearProps: 'transform' }, 0.55)
  tl.from(q('.plan-cta'), { opacity: 0, y: 10, duration: 0.5, stagger: 0.12, clearProps: 'transform' }, 0.95)
}, { trigger: '.pricing-panel' })

const DASHBOARD_URL = 'https://app.clarify.bot'
const SNOWFLAKE = /^\d{17,20}$/

// Discord-Store-Link aus der Runtime-Config (NUXT_PUBLIC_DISCORD_APPLICATION_ID,
// NUXT_PUBLIC_DISCORD_PREMIUM_SKU_ID). Fehlt eine der IDs, führt der CTA ins Dashboard.
const config = useRuntimeConfig()
const premiumUrl = computed(() => {
  const appId = String(config.public.discordApplicationId ?? '').trim()
  const skuId = String(config.public.discordPremiumSkuId ?? '').trim()
  return SNOWFLAKE.test(appId) && SNOWFLAKE.test(skuId)
    ? `https://discord.com/application-directory/${appId}/store/${skuId}`
    : DASHBOARD_URL
})

interface Plan {
  name: string
  billing: string
  description: string
  features: string[]
  cta: { label: string, to: string, primary?: boolean, external?: boolean }
  highlight?: boolean
}

const plans = computed<Plan[]>(() => [
  {
    name: 'Free',
    billing: 'No payment method required',
    description: 'Everything you need to run support on your server.',
    features: [
      'Tickets in DMs, private threads or channels, mirrored to a staff forum',
      'All modules: Feedback, Transcripts, Escalation, Opening hours, Assignment',
      'Integrations: Stripe, PayPal, Tebex, Patreon, GitHub, Webhooks',
      'Data Connectors for your own systems',
      'Statistics, ratings & agent leaderboard'
    ],
    cta: { label: 'Start for free', to: DASHBOARD_URL, external: true }
  },
  {
    name: 'Premium',
    billing: 'Server subscription in the Discord store',
    description: 'For communities that want Clarify under their own name.',
    features: [
      'Everything in Free',
      '10 feature tokens for modules instead of 5',
      'White label: your own bot name and avatar',
      'Hosted by us, no server of your own',
      'Manage or cancel the subscription in Discord'
    ],
    cta: { label: 'Get Premium', to: premiumUrl.value, primary: true, external: true },
    highlight: true
  }
])
</script>

<template>
  <section id="plans" ref="sectionRef" class="pricing">
    <!-- Alter Anker: externe Links auf /#preise landen weiterhin hier -->
    <span id="preise" class="legacy-anchor" aria-hidden="true" />
    <span class="section-rule" aria-hidden="true" />
    <div class="pricing-glow" aria-hidden="true" />
    <div class="section-shell">
      <div class="section-head">
        <h2 class="h-section pricing-title" data-split>Fair for small servers. Strong for big ones.</h2>
        <p class="copy-muted pricing-intro reveal">
          Start free with every module and integration. Go Premium when you want Clarify to run as your own bot.
        </p>
      </div>

      <div class="card-shell pricing-panel entrance">
        <div
          v-for="plan in plans"
          :key="plan.name"
          class="pricing-plan spot-card"
          :class="{ 'is-popular': plan.highlight }"
        >
          <div class="plan-head">
            <h3 class="plan-name">{{ plan.name }}</h3>
          </div>

          <p class="plan-billing">{{ plan.billing }}</p>

          <p class="plan-description copy-muted">{{ plan.description }}</p>

          <ul class="plan-features">
            <li v-for="f in plan.features" :key="f" class="plan-feature">
              <UIcon name="i-lucide-check" class="plan-check" aria-hidden="true" />
              {{ f }}
            </li>
          </ul>

          <UButton
            :label="plan.cta.label"
            :to="plan.cta.to"
            :target="plan.cta.external ? '_blank' : undefined"
            :color="plan.cta.primary ? 'primary' : 'neutral'"
            :variant="plan.cta.primary ? 'solid' : 'outline'"
            size="lg"
            block
            class="plan-cta"
            :class="{ 'plan-cta-primary': plan.cta.primary }"
          />
        </div>
      </div>

      <p class="pricing-footnote copy-muted reveal shell-gutter">
        Price and taxes are shown in the Discord store. Manage or cancel your subscription in Discord.
      </p>
    </div>
  </section>
</template>

<style scoped>
.pricing {
  position: relative;
  padding: var(--section-gap) 0 0;
}

/* Weicher Akzent-Schein hinter dem Panel — volle Breite, nach oben gefedert */
.pricing-glow {
  position: absolute;
  inset: auto 0 -20%;
  height: 70%;
  background: radial-gradient(58% 62% at 50% 100%, var(--nb-accent-tint), transparent 72%);
  pointer-events: none;
}

.legacy-anchor {
  position: absolute;
  top: 0;
}

/* Zwei Zeilen „Fair for small servers. / Strong for big ones." — über die
   Breite statt <br> (SplitText-Zeilenmasken vertragen keine harten Umbrüche) */
.pricing-title {
  max-width: 12em;
}

.pricing-intro {
  max-width: 52ch;
}

/* Ein Panel, zwei Spalten — Hairlines statt Karten-Schwarm */
.pricing-panel {
  display: grid;
  grid-template-columns: 1fr;
  margin-top: 72px;
  overflow: hidden;
}

.pricing-plan {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 34px 30px;
  border-top: 1px solid var(--nb-hairline);
}

.pricing-plan:first-child {
  border-top: 0;
}

.pricing-plan.is-popular {
  background:
    radial-gradient(120% 100% at 50% 0%, var(--nb-accent-tint), transparent 70%),
    var(--nb-surface-soft);
}

.plan-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.plan-name {
  font-size: 1.02rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--nb-ink);
}

.plan-billing {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--nb-ink-faint);
}

.plan-description {
  font-size: 0.92rem;
  min-height: 2.9em;
}

.plan-features {
  display: flex;
  flex-direction: column;
  gap: 11px;
  margin-block: 4px 10px;
}

.plan-feature {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.92rem;
  line-height: 1.45;
  color: var(--nb-ink-muted);
}

.plan-check {
  width: 16px;
  height: 16px;
  margin-top: 3px;
  color: var(--nb-accent-soft);
  flex-shrink: 0;
}

.plan-cta {
  margin-top: auto;
  border-radius: var(--radius-pill);
  justify-content: center;
}

.plan-cta-primary {
  background: var(--nb-accent);
  color: #fff;
  transition:
    background var(--dur) var(--ease-out),
    transform var(--dur) var(--ease-out);
}

.plan-cta-primary:hover {
  background: var(--nb-accent-hover);
  transform: translateY(-1px);
}

.plan-cta-primary:active {
  transform: translateY(0) scale(0.98);
}

.pricing-footnote {
  margin-top: 26px;
  font-size: 0.85rem;
  text-align: center;
}

@media (min-width: 900px) {
  .pricing-panel {
    grid-template-columns: repeat(2, 1fr);
  }

  .pricing-plan {
    border-top: 0;
    border-left: 1px solid var(--nb-hairline);
    padding: 38px 34px;
  }

  .pricing-plan:first-child {
    border-left: 0;
  }
}
</style>
